<template>
  <DefinePanel>
    <DatepickerMonthYearPanel
      v-if="monthPicker || yearPicker"
      v-model:view="monthYearView"
      :year="selectedMonth?.year ?? selectedYear ?? new Date().getFullYear()"
      :selected-month="selectedMonth"
      :selected-year="selectedYear"
      :min-date="minDateValue"
      :max-date="maxDateValue"
      :year-only="yearPicker"
      @month-selected="handleMonthSelected"
      @year-selected="handleYearSelected"
    />
    <DatepickerCalendar
      v-else
      v-model:active-edge="activeEdge"
      :start-date="selectedDates[0]"
      :end-date="selectedDates[1]"
      :range="range"
      :enable-time-picker="enableTimePicker"
      :months="visibleMonthCount"
      :week-numbers="weekNumbers"
      :min-date="minDateValue"
      :max-date="maxDateValue"
      :disabled-dates="disabledDates"
      :highlighted-dates="highlightedDates"
      :no-disabled-range="noDisabledRange"
      @select="emitValue"
      @select-range="(startDate, endDate) => emitValue([startDate, endDate])"
      @day-selected="handleDaySelected"
    />
  </DefinePanel>

  <div
    v-if="inline"
    v-bind="$attrs"
    :class="float({ class: 'border border-obsidian-200 p-4' })"
    data-testid="datepicker-inline"
  >
    <ReusePanel />
  </div>
  <InputLabel
    v-else
    v-bind="$attrs"
    :label="label"
    :tooltip="tooltip"
    :is-error="isError"
    :warning="warning"
  >
    <div ref="reference" class="relative" @keydown.esc="handleEscape">
      <CalendarIcon
        class="pointer-events-none absolute left-2 top-1/2 size-4 -translate-y-1/2 text-hazel-500"
      />
      <input
        ref="inputElement"
        type="text"
        autocomplete="off"
        :value="inputText"
        :placeholder="placeholder ?? (range ? undefined : dateFormat.toUpperCase())"
        :readonly="range"
        :disabled="disabled"
        :class="input({ size: 'sm', class: ['pl-8', showClearButton && 'pr-8'] })"
        :data-error="isError ? 'true' : undefined"
        @click="open"
        @focus="open"
        @input="inputText = ($event.target as HTMLInputElement).value"
        @blur="commitInputText"
        @keydown.enter.prevent="handleEnter"
        @keydown.tab="close"
      />
      <button
        v-if="showClearButton"
        type="button"
        class="absolute right-2 top-1/2 -translate-y-1/2 text-obsidian-400 hover:text-obsidian-600"
        :aria-label="t('clear')"
        data-testid="datepicker-clear"
        @click.prevent="clear"
      >
        <XIcon class="size-4" />
      </button>
    </div>
    <InputHint :hint="hint" :is-error="isError" />
  </InputLabel>
  <!-- Outside the label, so clicks in the panel don't re-activate the input. -->
  <div
    v-if="!inline && isOpen"
    ref="floating"
    :style="floatingStyles"
    :class="float({ class: 'z-[1100] p-4' })"
    data-testid="datepicker-popover"
    @keydown.esc="handleEscape"
  >
    <ReusePanel />
  </div>
</template>

<script lang="ts" setup generic="T extends DatepickerModelValue">
import { computed, ref, useTemplateRef, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { createReusableTemplate, onClickOutside, onKeyStroke, useBreakpoints } from '@vueuse/core';
import { format, isValid, parse } from 'date-fns';
import { CalendarIcon, XIcon } from 'lucide-vue-next';
import { input } from '@carvia/theme/lib/cva/input';
import { float } from '@carvia/theme/lib/cva/float';
import { getDateFormat, getDateTimeFormat } from '@/hooks/use-date-format';
import { cvBreakpoints } from '@/utils/breakpoints';
import InputLabel from './InputLabel.vue';
import InputHint from './InputHint.vue';
import DatepickerCalendar from './datepicker/DatepickerCalendar.vue';
import DatepickerMonthYearPanel from './datepicker/DatepickerMonthYearPanel.vue';
import { useDatepickerFloating } from './datepicker/use-datepicker-floating';
import {
  applyTimeOfDay,
  getDateFnsLocale,
  isDayDisabled,
  toDate,
  type DatepickerModelValue,
  type DateLike,
  type DisabledDates,
  type MonthValue,
} from './datepicker/datepicker-utils';

defineOptions({ inheritAttrs: false });

const props = withDefaults(
  defineProps<{
    /**
     * Date (single), `[start, end]` (range; `end` is null while a new start awaits its end), `{ month, year }` (month picker, zero-based month)
     * or a year number (year picker). Emits `null` when cleared.
     */
    modelValue?: T;
    label?: string;
    hint?: string;
    isError?: boolean;
    warning?: string;
    tooltip?: string;
    placeholder?: string;
    /** date-fns format for the input. Defaults to the locale date (and time) format. */
    dateFormat?: string;
    minDate?: DateLike;
    maxDate?: DateLike;
    range?: boolean;
    enableTimePicker?: boolean;
    monthPicker?: boolean;
    yearPicker?: boolean;
    /** Render the calendar without input and popover. */
    inline?: boolean;
    weekNumbers?: boolean;
    disabled?: boolean;
    clearable?: boolean;
    disabledDates?: DisabledDates;
    highlightedDates?: (date: Date) => boolean;
    noDisabledRange?: boolean;
  }>(),
  { clearable: true, modelValue: undefined },
);

const emit = defineEmits<{
  (e: 'update:modelValue', value: T): void;
  /** A day was picked; in range mode only once the end is picked. */
  (e: 'daySelected'): void;
}>();

/** Range only: which edge the next day click sets. Range fields bind this to the clicked field. */
const activeEdge = defineModel<'start' | 'end'>('activeEdge', { default: 'start' });

const { t, locale } = useI18n();
const [DefinePanel, ReusePanel] = createReusableTemplate();

const isWideLayout = useBreakpoints(cvBreakpoints).greaterOrEqual('md');
const visibleMonthCount = computed(() => (props.range && isWideLayout.value ? 2 : 1));

const minDateValue = computed(() => toDate(props.minDate));
const maxDateValue = computed(() => toDate(props.maxDate));

const dateFormat = computed(() => {
  if (props.dateFormat) return props.dateFormat;
  if (props.monthPicker) return 'MM/yyyy';
  if (props.yearPicker) return 'yyyy';
  return props.enableTimePicker ? getDateTimeFormat() : getDateFormat();
});
const formatOptions = computed(() => ({ locale: getDateFnsLocale(locale.value) }));

const selectedDates = computed((): [Date | null, Date | null] => {
  const value = props.modelValue;
  if (Array.isArray(value)) return [toDate(value[0]), toDate(value[1])];
  if (value instanceof Date || typeof value === 'string') return [toDate(value), null];
  return [null, null];
});

const selectedMonth = computed(() => {
  const value = props.modelValue;
  return value && typeof value === 'object' && 'month' in value ? (value as MonthValue) : null;
});

const selectedYear = computed(() =>
  typeof props.modelValue === 'number' ? props.modelValue : null,
);

const formatDateValue = (date: Date) => format(date, dateFormat.value, formatOptions.value);

const formattedValue = computed(() => {
  if (props.monthPicker) {
    const month = selectedMonth.value;
    return month ? formatDateValue(new Date(month.year, month.month, 1)) : '';
  }
  if (props.yearPicker) {
    return selectedYear.value ? String(selectedYear.value) : '';
  }
  const [startDate, endDate] = selectedDates.value;
  if (!props.range) return startDate ? formatDateValue(startDate) : '';
  if (!startDate) return '';
  return `${formatDateValue(startDate)} – ${endDate ? formatDateValue(endDate) : '…'}`;
});

const inputText = ref('');
watch(formattedValue, (value) => (inputText.value = value), { immediate: true });

const hasValue = computed(() => !!formattedValue.value);
const showClearButton = computed(() => props.clearable && hasValue.value && !props.disabled);

const emitValue = (value: Date | [Date, Date | null] | MonthValue | number | null) => {
  emit('update:modelValue', value as T);
};

const clear = () => {
  emitValue(null);
  inputText.value = '';
};

const commitInputText = () => {
  const text = inputText.value.trim();
  if (props.range || text === formattedValue.value) return;
  if (!text) {
    if (props.clearable) {
      clear();
    } else {
      inputText.value = formattedValue.value;
    }
    return;
  }

  const parsed = parse(text, dateFormat.value, new Date(), formatOptions.value);
  if (!isValid(parsed)) {
    inputText.value = formattedValue.value;
    return;
  }

  if (props.monthPicker) {
    emitValue({ month: parsed.getMonth(), year: parsed.getFullYear() });
  } else if (props.yearPicker) {
    emitValue(parsed.getFullYear());
  } else {
    const hasTimeInFormat = /[Hhkm]/.test(dateFormat.value);
    const date = hasTimeInFormat ? parsed : applyTimeOfDay(parsed, selectedDates.value[0]);
    const isAllowed = !isDayDisabled(date, {
      minDate: minDateValue.value,
      maxDate: maxDateValue.value,
      disabledDates: props.disabledDates,
    });
    if (!isAllowed) {
      inputText.value = formattedValue.value;
      return;
    }
    emitValue(date);
  }
  // Re-format typed shorthand such as "1.2.2025" once the model echoes back.
  inputText.value = formattedValue.value;
};

const isOpen = ref(false);
const monthYearView = ref<'months' | 'years'>(props.yearPicker ? 'years' : 'months');
const reference = useTemplateRef<HTMLElement>('reference');
const floating = useTemplateRef<HTMLElement>('floating');
const inputElement = useTemplateRef<HTMLInputElement>('inputElement');

const floatingStyles = useDatepickerFloating(reference, floating);

const open = () => {
  if (props.disabled || isOpen.value) return;
  monthYearView.value = props.yearPicker ? 'years' : 'months';
  activeEdge.value = 'start';
  isOpen.value = true;
};

const close = () => {
  isOpen.value = false;
};

const handleEnter = () => {
  commitInputText();
  close();
};

onClickOutside(floating, close, { ignore: [reference] });
// Stop Escape from inside the picker so a surrounding modal stays open; it listens on its own element.
const handleEscape = (event: KeyboardEvent) => {
  if (!isOpen.value) return;
  event.stopPropagation();
  close();
};
// Fallback when focus is outside the picker, e.g. on the page body.
onKeyStroke('Escape', () => isOpen.value && close());

const handleDaySelected = () => {
  emit('daySelected');
  if (!props.inline && !props.enableTimePicker) {
    close();
    inputElement.value?.blur();
  }
};

const handleMonthSelected = (value: MonthValue) => {
  emitValue(value);
  close();
};

const handleYearSelected = (year: number) => {
  emitValue(year);
  close();
};
</script>

<i18n lang="json">
{
  "en": {
    "clear": "Clear"
  },
  "de": {
    "clear": "Leeren"
  },
  "es": {
    "clear": "Borrar"
  }
}
</i18n>
